/**Dependencies */
import React, { useRef } from "react";

/**Components */
import { Button, Row, Col } from "react-bootstrap";

/**Styles */
import "../styles/components/paxSelector.scss";
import { useOnClickOutside } from "../hooks/useOnClickOutside";

/**
 * Componente que permite al usuario seleccionar la cantidad de adultos y niños
 * por habitación, y agregar o eliminar habitaciones según sea necesario.
 *
 * @param {boolean} showPaxSelector - Indica si el selector de pasajeros debe mostrarse.
 * @param {Function} handleClosePaxSelector - Función para manejar el cierre del selector de pasajeros.
 *
 * @returns {JSX.Element} - Retorna el componente de selección de pasajeros.
 */

export const PaxSelector = ({
  showSelector,
  setShowSelector,
  roomsData,
  personData,
  setArrRooms,
  setArrPersons,
  isHotel,
  isAct,
  isHome,
  isTraslados,
}) => {
  const ref = useRef();

  useOnClickOutside(ref, () => {
    setShowSelector("none");
  });

  /**
   * Agrega una habitación a la lista de habitaciones seleccionadas.
   *
   * @param {Event} e - El evento de click en el botón de agregar habitación.
   */
  const addRoom = (e) => {
    e.preventDefault();
    if (roomsData.length < 9) {
      setArrRooms([
        ...roomsData,
        { id: roomsData.length + 1, adults: 1, minors: 0, ages: [] },
      ]);
    }
  };

  /**
   * Elimina la última habitación de la lista de habitaciones seleccionadas.
   *
   * @param {Event} e - El evento de click en el botón de eliminar habitación.
   */
  const removeLastRoom = (e) => {
    e.preventDefault();
    if (roomsData.length > 1) {
      setArrRooms(roomsData.slice(0, -1)); // Elimina la última habitación
    }
  };

  /**
   * Actualiza la cantidad de adultos o menores en una habitación.
   *
   * @param {number} id - El ID de la habitación a actualizar.
   * @param {"adults"|"minors"} type - El tipo de huésped a actualizar (adultos o menores).
   * @param {number} delta - El delta de la cantidad de huéspedes a agregar o eliminar.
   */

  if (isHotel) {
  }
  const updateGuests = (id, type, delta) => {
    if (isHotel) {
      setArrRooms(
        roomsData.map((room, key) => {
          if (key === id) {
            const totalGuests = room.adults + room.minors; // Total de personas en la habitación
            const newCount = room[type] + delta;

            // Restricción: máximo 4 pax en total
            if (totalGuests + delta > 4 || newCount < 0) {
              return room;
            }

            if (type === "minors") {
              const newAges =
                newCount > room.ages.length
                  ? [...room.ages, 0] // Agrega un menor con edad inicial 0
                  : room.ages.slice(0, newCount); // Elimina edades excedentes
              return { ...room, minors: newCount, ages: newAges };
            }

            return { ...room, [type]: newCount };
          }
          return room;
        })
      );
    } else if (isAct) {
      setArrPersons(
        personData.map((person) => {
          if (person.id === id) {
            const totalGuests = person.adults + person.minors; // Total de personas en la habitación
            const newCount = person[type] + delta;
            // Restricción: máximo 4 pax en total
            if (totalGuests + delta > 8 || newCount < 0) {
              return person;
            }

            if (type === "minors") {
              const newAges =
                newCount > person.ages.length
                  ? [...person.ages, 0] // Agrega un menor con edad inicial 0
                  : person.ages.slice(0, newCount); // Elimina edades excedentes
              return { ...person, minors: newCount, ages: newAges };
            }

            if (type === "adults") {
              const newAgesAdults =
                newCount > person.agesAdults.length
                  ? [...person.agesAdults, 21] // Agrega un menor con edad inicial 0
                  : person.agesAdults.slice(0, newCount); // Elimina edades excedentes

              return { ...person, adults: newCount, agesAdults: newAgesAdults };
            }

            return { ...person, [type]: newCount };
          }
          return person;
        })
      );
    } else if (isTraslados) {
      setArrPersons(
        personData.map((person) => {
          if (person.id === id) {
            const totalGuests =
              person?.adults + person?.minors + person?.infants;
            const newCount = person[type] + delta;

            if (totalGuests + delta > 32 || newCount < 0) {
              return person;
            }

            if (type === "minors") {
              const newAges =
                newCount > person.ages.length
                  ? [...person.ages, 0]
                  : person.ages.slice(0, newCount);
              return { ...person, minors: newCount, ages: newAges };
            }

            if (type === "infants") {
              const newAges =
                newCount > person.agesInfants.length
                  ? [...person.agesInfants, 0]
                  : person.agesInfants.slice(0, newCount);
              return { ...person, infants: newCount, agesInfants: newAges };
            }

            if (type === "adults") {
              const newAgesAdults =
                newCount > person.agesAdults.length
                  ? [...person.agesAdults, 21]
                  : person.agesAdults.slice(0, newCount);
              return { ...person, adults: newCount, agesAdults: newAgesAdults };
            }

            return { ...person, [type]: newCount };
          }
          return person;
        })
      );
    }
  };

  /**
   * Updates the age of a minor in a specific room.
   *
   * @param {number} roomId - The ID of the room where the minor's age needs to be updated.
   * @param {number} index - The index of the minor in the room's ages array.
   * @param {number} age - The new age to be set for the minor.
   */

  const updateMinorAge = (roomId, index, age) => {
    if (isHotel) {
      setArrRooms(
        roomsData.map((room, key) =>
          key === roomId
            ? {
                ...room,
                ages: room.ages.map((a, i) => (i === index ? age : a)),
              }
            : room
        )
      );
    } else if (isAct) {
      setArrPersons(
        personData.map((person) =>
          person.id === roomId
            ? {
                ...person,
                ages: person.ages.map((a, i) => (i === index ? age : a)),
              }
            : person
        )
      );
    }
  };

  return (
    <div>
      {isHotel ? (
        <div
          className={
            "pax-selector-hotel p-2 border shadow-sm bg-white" +
            (isHome ? "" : " position-container")
          }
          /* style={{
            display: showSelector,
            borderRadius: "35px",
            width: "290px",
            zIndex: "9999999",
            position: "absolute",
          }} */
           style={
            !isHome
              ? {
                  display: showSelector,
                  borderRadius: "35px",
                  width: "290px",
                  zIndex: "9999999",
                  position: "absolute",
                }
              : {
                  display: showSelector,
                  borderRadius: "35px",
                  width: "280px",
                  zIndex: "9999999",
                  position: "absolute",
                }
          }
        >
          <div
            className="mt-1 p-2 border-hab"
            //style={{ maxHeight: "17rem", overflowY: "auto" }}
          >
            {roomsData.map((room, key) => (
              <div key={key} className="border-hab mb-3">
                <h6>Habitación {key + 1} </h6>
                <Row className="align-items-center mb-2">
                  <Col xs={6}>
                    <p className="mb-0 fw-bold">Adultos</p>
                    <small>Desde 12</small>
                  </Col>
                  <Col
                    xs={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <Button
                      variant="outline-secondary"
                      data-habitacion={room.room}
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(key, "adults", -1);
                      }}
                      disabled={room.adults + room.minors <= 1}
                    >
                      −
                    </Button>
                    <span className="mx-2">{room.adults}</span>
                    <Button
                      variant="outline-secondary"
                      data-habitacion={room.room}
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(key, "adults", 1);
                      }}
                      disabled={room.adults + room.minors >= 4}
                    >
                      +
                    </Button>
                  </Col>
                </Row>

                <Row className="align-items-center border-bottom-1">
                  <Col xs={6}>
                    <p className="mb-0 fw-bold">Menores</p>
                    <small>Hasta 11</small>
                  </Col>
                  <Col
                    xs={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <Button
                      variant="outline-secondary"
                      data-habitacion="1"
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(key, "minors", -1);
                      }}
                    >
                      −
                    </Button>
                    <span className="mx-2">{room.minors}</span>
                    <Button
                      variant="outline-secondary"
                      data-habitacion="1"
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(key, "minors", 1);
                      }}
                    >
                      +
                    </Button>
                  </Col>
                </Row>

                {room.minors > 0 && (
                  <div className="row div-menores-hab1 mt-3">
                    {room.ages.map((age, index) => (
                      <div className="col menor menor1-hab1">
                        <h6>Edad menor {index + 1}</h6>
                        <select
                          className="form-control"
                          name="edadmenor1-hab1"
                          value={age}
                          onChange={(e) =>
                            updateMinorAge(
                              key,
                              index,
                              parseInt(e.target.value)
                            )
                          }
                        >
                          {[...Array(18).keys()].map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-3 bg-white">
            <Button
              variant="link"
              className="text-primary"
              onClick={(e) => addRoom(e)}
            >
              Agregar
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="ms-2"
              onClick={(e) => removeLastRoom(e)}
            >
              Eliminar
            </Button>

            <div className="mt-3 text-center">
              <Button
                style={{ width: "100%", backgroundColor: "#3259a8" }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowSelector("none");
                }}
              >
                Listo
              </Button>
            </div>
          </div>
        </div>
      ) : isAct ? (
        <div
          className={
            "pax-selector-act p-3 shadow-sm" +
            (isHome ? "" : " position-container")
          }
          style={
            !isHome
              ? {
                  display: showSelector,
                  borderRadius: "35px",
                  width: "300px",
                  left: "0",
                  top: "50px",
                  position: "absolute",
                  zIndex: "9999999",
                }
              : {
                  display: showSelector,
                  borderRadius: "35px",
                  width: "300px",
                  left: "0",
                  position: "absolute",
                  zIndex: "9999999",
                }
          }
        >
          <div className="pax-selector p-2 border shadow-sm"
            style={{
              borderRadius: "35px",
              width: "300px",
              zIndex: "9999999",
              position: "absolute",
            }}
          >
            <div className="mt-1 p-2 border-hab"
              //style={{ maxHeight: "17rem", overflowY: "auto" }}
            >
            {personData.map((personInfo, index) => (
              <div key={index} className="border-hab mb-3">
                <h6>Personas</h6>
                <Row className="align-items-center mb-2">
                  <Col xs={6}>
                    <p className="mb-0 fw-bold">Adultos</p>
                    <small>Desde 12</small>
                  </Col>
                  <Col
                    xs={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <Button
                      variant="outline-secondary"
                      data-habitacion={personInfo.adults}
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(personInfo.id, "adults", -1);
                      }}
                      disabled={personInfo.adults + personInfo.minors <= 1}
                    >
                      −
                    </Button>
                    <span className="mx-2">{personInfo.adults}</span>
                    <Button
                      variant="outline-secondary"
                      data-habitacion={personInfo.adults}
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(personInfo.id, "adults", 1);
                      }}
                      disabled={personInfo.adults + personInfo.minors >= 8}
                    >
                      +
                    </Button>
                  </Col>
                </Row>

                <Row className="align-items-center border-bottom-1">
                  <Col xs={6}>
                    <p className="mb-0 fw-bold">Menores</p>
                    <small>Hasta 11</small>
                  </Col>
                  <Col
                    xs={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <Button
                      variant="outline-secondary"
                      data-habitacion="1"
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(personInfo.id, "minors", -1);
                      }}
                    >
                      −
                    </Button>
                    <span className="mx-2">{personInfo.minors}</span>
                    <Button
                      variant="outline-secondary"
                      data-habitacion="1"
                      onClick={(e) => {
                        e.preventDefault();
                        updateGuests(personInfo.id, "minors", 1);
                      }}
                    >
                      +
                    </Button>
                  </Col>
                </Row>

                {personInfo.minors > 0 && (
                  <div className="row div-menores-hab1 mt-3">
                    {personInfo.ages.map((age, index) => (
                      <div className="col menor menor1-hab1">
                        <h6>Edad menor {index + 1}</h6>
                        <select
                          className="form-control"
                          name="edadmenor1-hab1"
                          value={age}
                          onChange={(e) =>
                            updateMinorAge(
                              personInfo.id,
                              index,
                              parseInt(e.target.value)
                            )
                          }
                        >
                          {[...Array(18).keys()].map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
            <div className="p-3">
              <Button
                style={{ width: "100%", backgroundColor: "#3259a8" }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowSelector("none");
                }}
              >
                Listo
              </Button>
            </div>
          </div>
        </div>
      ) : (
        isTraslados && (
          <div
            className={
              "pax-selector-tras p-3 border shadow-sm bg-white pax-traslados" +
              (isHome ? "" : " position-container")
            }
            style={
              !isHome
                ? {
                    display: showSelector,
                    borderRadius: "35px",
                    width: "300px",
                    top: "50px",
                    position: "absolute",
                    zIndex: "9999999",
                  }
                : {
                    display: showSelector,
                    borderRadius: "35px",
                    width: "300px",
                    position: "absolute",
                    zIndex: "9999999",
                  }
            }
          >
            <div
              className="mt-3 p-3 border rounded"
              //style={{ maxHeight: "20rem", overflowY: "auto" }}
            >
              {personData.map((personInfo, index) => (
                <div key={index} className="border-hab mb-3">
                  <h6>Personas</h6>
                  <Row className="align-items-center mb-2">
                    <Col xs={6}>
                      <p className="mb-0 fw-bold">Adultos</p>
                      <small className="form-text" style={{ fontSize: "12px" }}>
                        (13 a 99 años)
                      </small>
                    </Col>
                    <Col
                      xs={6}
                      className="d-flex align-items-center justify-content-end"
                    >
                      <Button
                        variant="outline-secondary"
                        data-habitacion={personInfo.adults}
                        onClick={(e) => {
                          e.preventDefault();
                          updateGuests(personInfo?.id, "adults", -1);
                        }}
                        disabled={personInfo?.adults + personInfo?.minors <= 1}
                      >
                        −
                      </Button>
                      <span className="mx-2">{personInfo.adults}</span>
                      <Button
                        variant="outline-secondary"
                        data-habitacion={personInfo.adults}
                        onClick={(e) => {
                          e.preventDefault();
                          updateGuests(personInfo?.id, "adults", 1);
                        }}
                        disabled={personInfo?.adults + personInfo?.minors >= 32}
                      >
                        +
                      </Button>
                    </Col>
                  </Row>

                  <Row className="align-items-center border-bottom-1">
                    <Col xs={6}>
                      <p className="mb-0 fw-bold">Menores</p>
                      <small className="form-text" style={{ fontSize: "12px" }}>
                        (3 a 12 años)
                      </small>
                    </Col>
                    <Col
                      xs={6}
                      className="d-flex align-items-center justify-content-end"
                    >
                      <Button
                        variant="outline-secondary"
                        data-habitacion="1"
                        onClick={(e) => {
                          e.preventDefault();
                          updateGuests(personInfo?.id, "minors", -1);
                        }}
                      >
                        −
                      </Button>
                      <span className="mx-2">{personInfo.minors}</span>
                      <Button
                        variant="outline-secondary"
                        data-habitacion="1"
                        onClick={(e) => {
                          e.preventDefault();
                          updateGuests(personInfo?.id, "minors", 1);
                        }}
                      >
                        +
                      </Button>
                    </Col>
                  </Row>
                  <Row className="align-items-center border-bottom-1">
                    <Col xs={6}>
                      <p className="mb-0 fw-bold">Infantes</p>
                      <small className="form-text" style={{ fontSize: "12px" }}>
                        (0 a 2 años)
                      </small>
                    </Col>
                    <Col
                      xs={6}
                      className="d-flex align-items-center justify-content-end"
                    >
                      <Button
                        variant="outline-secondary"
                        data-habitacion="1"
                        onClick={(e) => {
                          e.preventDefault();
                          updateGuests(personInfo?.id, "infants", -1);
                        }}
                      >
                        −
                      </Button>
                      <span className="mx-2">{personInfo?.infants}</span>
                      <Button
                        variant="outline-secondary"
                        data-habitacion="1"
                        onClick={(e) => {
                          e.preventDefault();
                          updateGuests(personInfo?.id, "infants", 1);
                        }}
                      >
                        +
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
              <div className="mt-4 text-center">
                <Button
                  style={{ width: "100%", backgroundColor: "#3259a8" }}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowSelector("none");
                  }}
                >
                  Listo
                </Button>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};
